import {
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Record } from 'src/app/models/record';
import { Subscription } from 'rxjs';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Router } from '@angular/router';
import { Collection } from 'src/app/models/collection';
import { SearchModel, SearchState } from 'src/app/models/search-model';
import { SearchTerm } from 'src/app/state/user/searches/models/search';
import { Util } from 'src/app/util';
import { OtkList } from '../../models/user-item-list';
import { SliderSubheaderDirective } from 'src/app/directives/slider-subheader.directive';
import { SliderActionsDirective } from 'src/app/directives/slider-actions.directive';
import { RecordSwipperItem, RecordSwipperUtils, SwiperConfigType } from 'src/app/models/record-swiper';
import { Location } from '@angular/common';
import { SliderLoadedView } from '../../models/slider-loaded-view';
import { HomeFacade } from '../../state/home/home.facade';
import { UserListFacade } from '../../state/user-list/user-list.facade';
import { ActiveRecordFacade } from '../../state/core/active-record-facade';
import { AuthService } from '../../services/auth.service';
import { PlayerUrlAnnotationService } from '../../services/api/player-url-annotation.service';
import { UserFacade } from 'src/app/_core/state/user/user.facade';
import { AnalyticsService } from 'src/app/_core/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config.service';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
})
export class SliderComponent implements OnInit, OnChanges {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly userFacade = inject(UserFacade);
  private readonly configService = inject(ConfigService);

  @Input()
  items: Array<Record | OtkList>;

  @Input()
  title: string;

  @Input()
  id: number;

  @Input()
  swiperConfigType: SwiperConfigType;

  @Input()
  featured = false;

  @Input()
  userSlider = false;

  @Input()
  preview = false;

  @Input()
  placeholder = false;

  @Input()
  collection: Partial<Collection>;

  @Input()
  showSubheader: boolean;

  @Input()
  showActions: boolean;

  @Input()
  renderWithZeroItems = false;

  @Input()
  isFavouritesSlider: boolean;

  @Input()
  indentTop: boolean;

  @Input()
  showEmptyBoard = false;

  @Input()
  extraHeaderMargin = true;

  @Input()
  subtitle = false;

  @Input()
  loadedView: SliderLoadedView;

  @Output()
  selected = new EventEmitter<Record>();

  @Output()
  titleClicked = new EventEmitter<void>();

  @ContentChild(SliderSubheaderDirective, { static: false })
  subheader!: SliderSubheaderDirective;

  @ContentChild(SliderActionsDirective, { static: false })
  actions!: SliderActionsDirective;

  selectedRecord: Record;
  visible = false;
  fadeAnimation: string;
  showSlider: boolean;
  ioMissing$: Subscription;
  swiperItems: RecordSwipperItem[] = [];
  selectedItemIndex: number;

  constructor(
    protected broadcastService: BroadcastService,
    protected router: Router,
    private _location: Location,
    private readonly homeFacade: HomeFacade,
    private readonly userListFacade: UserListFacade,
    private readonly authService: AuthService,
    private readonly playerUrlAnnotationService: PlayerUrlAnnotationService
  ) {}

  ngOnInit() {
    const activeRecord = this.getFacade().activeRecord;
    // Set selected item index when coming back from other page to show ^ when info item opens
    if (activeRecord && (this.collection?.id ?? 0) === activeRecord.collectionId) {
      this.selectedItemIndex = activeRecord?.sliderIndex;
    }

    this.buildSwiperItems();

    setTimeout(() => {
      const records = this.items as Record[];
      const action = Util.getAction();
      const user = this.userFacade.user;
      if (action && records && user) {
        const recordIndex = records.findIndex((record) => record.id === action.recordId);

        if (recordIndex >= 0) {
          this.broadcastService.broadcastSliderItem(records[recordIndex]);
          this.selected.emit(records[recordIndex]);

          this.selectedItemIndex = recordIndex;
          this.selectedRecord = records[recordIndex];

          setTimeout(() => {
            Util.runAction(user, this.playerUrlAnnotationService, this.selectedRecord);
          }, 1000);

          return;
        }
      }
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges) {
    const col = changes.collection;

    if (col && col.currentValue.fromUserList) {
      this.preview = false;
    }

    const placeholder = changes.placeholder;

    if (placeholder && placeholder.currentValue === false) {
      this.refreshSwiperItems();
    }
  }

  buildSwiperItems() {
    if (this.items && this.items.length > 0) {
      if (this.collection && this.collection.fromUserList) {
        this.items.forEach((item) =>
          this.swiperItems.push(RecordSwipperUtils.fromOtkListToSwiperItem(item as OtkList))
        );
      } else {
        this.items.forEach((item) => this.swiperItems.push(RecordSwipperUtils.fromRecordToSwiperItem(item as Record)));
      }
    }
  }

  refreshSwiperItems() {
    this.deleteSwiperItems();
    this.buildSwiperItems();
  }

  deleteSwiperItems() {
    this.swiperItems = [];
  }

  setVisible() {
    this.visible = true;

    if (this.placeholder) {
      this.fadeAnimation = 'visible';
    } else {
      this.fadeAnimation = 'hidden';
    }
  }

  removeItemByIndex(index: number) {
    if (this.selectedItemIndex === index) {
      this.selectedItemIndex = null;
      this.selectedRecord = null;
    }

    this.swiperItems.splice(index, 1);
  }

  queryCollection() {
    if (this.collection.fromUserList) {
      return;
    }

    let searchModel: SearchModel;

    if (this.collection.query) {
      const parts = this.collection.query.split(':');

      if (parts.length === 1) {
        parts.unshift(Util.ALLFIELDS);
      }

      if (Util.API_SEARCH_FIELDS.includes(parts[0])) {
        const searchTerm: SearchTerm = {
          field: parts[0],
          term: parts[1],
        };

        searchModel = new SearchModel(undefined, this.configService.get().resultsFilterOrder);
        searchModel.addTerm(searchTerm);
      }
    }

    const _queryParams = searchModel ? searchModel.toRequest() : { collectionId: this.id };

    if (this.collection.orderBy) {
      _queryParams.collectionId = this.id;
      _queryParams.order = this.collection.orderBy;
    }

    if (this.collection.topten) {
      _queryParams.topten = this.collection.topten;
    }

    this.analyticsService.searchPerformedOrigin = 'carousel';
    this.analyticsService.sendUserActionEvent('carousel_detail', true);

    this.router.navigate(['/results'], {
      queryParams: _queryParams,
    });
  }

  onSelected(clickedItemIndex: number) {
    if (this.collection && this.collection.fromUserList) {
      const selectedList = this.items[clickedItemIndex] as OtkList;
      this.router.navigate(['list', selectedList.id]);
    }

    if (this.selectedItemIndex === clickedItemIndex) {
      this.onCloseInfo();
      return;
    }

    const clickedSwiperItem = (this.items as Record[])[clickedItemIndex];

    if (clickedSwiperItem && clickedSwiperItem.wishListInfo) {
      const state = {
        prevNav: this._location.path(),
      } as SearchState;
      this.router.navigate(['/list', Util.slugifyListRecord(clickedSwiperItem)], { state });
    }

    this.broadcastService.broadcastSliderItem(clickedSwiperItem);
    this.selected.emit(clickedSwiperItem);

    if (this.preview && this.items != null) {
      this.selectedItemIndex = clickedItemIndex;
      this.selectedRecord = clickedSwiperItem;
      this.analyticsService.sendUserActionEvent('carousel_tdp', true);
    }
  }

  onCloseInfo() {
    this.selectedItemIndex = null;
    this.selectedRecord = null;
  }

  onTitleClicked() {
    this.titleClicked.emit();
  }

  exploreCatalog() {
    const model = new SearchModel(undefined, this.configService.get().resultsFilterOrder);
    model.explore = true;

    this.router.navigate(['/results'], {
      queryParams: model.toRequest(),
    });
  }

  private getFacade(): ActiveRecordFacade {
    switch (this.loadedView) {
      case 'HOME':
        return this.homeFacade;
      case 'USER_LISTS':
        return this.userListFacade;
      default:
        return this.homeFacade;
    }
  }
}
